import React, { FC, useCallback } from 'react';
import { convertEnumsToReadableStrings, formatFileSize } from 'utils/helpers';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Col, FMGModal, Popconfirm, Row, Typography } from 'components';
import { getFileIcon } from 'components/common/communicationHub/note';
import styles from 'components/common/tasks/styles.module.scss';
import { Image } from 'antd';
import { DeleteIcon } from 'components/common/icons';
import { useAttachmentDeleteMutation, useAttachmentFileDeleteMutation } from 'api';
import { useNotificationMessages } from 'hooks/useNotificationMessages';

interface IEntityDrawer {
  onExpand: () => void;
  drawerState: boolean;
  entityName: string;
  data?: Record<string, any>[];
}

export const AttachmentDrawer: FC<IEntityDrawer> = React.memo((props) => {
  const { onExpand, drawerState, entityName, data } = props;
  const { handleSuccess, handleError } = useNotificationMessages();
  const [attachmentDelete] = useAttachmentDeleteMutation();
  const [attachmentFileDelete] = useAttachmentFileDeleteMutation();

  const onDelete = useCallback(
    async (deletedId: string, fileName: string) => {
      try {
        const { data, error } = await attachmentDelete({
          entityName: entityName,
          id: deletedId,
          service: 'wrd',
        });
        if (data?.success) {
          if (data.data?.inherited) {
            handleSuccess('Attachment was successfully deleted.');
            onExpand();
            return;
          }
          const { data: fileData, error: fileError } = await attachmentFileDelete({
            id: deletedId,
            name: fileName,
          });
          if (fileData?.success) {
            handleSuccess('Attachment was successfully deleted.');
            onExpand();
          } else {
            handleError(fileError);
          }
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [handleError, handleSuccess, attachmentDelete, onExpand, attachmentFileDelete, entityName]
  );
  return (
    <FMGModal
      closeIcon={null}
      width={471}
      height={600}
      footer={<></>}
      open={drawerState}
      onClose={() => onExpand()}
      onCancel={() => onExpand()}
      styles={{
        header: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
      }}
      title={'Attachments'}
    >
      <div style={{ position: 'absolute', right: 16, top: 16 }}>
        <Button icon={<CloseOutlined />} onClick={() => onExpand()} type="text" />
      </div>
      <Row gutter={[0, 16]}>
        {data?.map((attachment) => (
          <Col
            key={attachment.id}
            span={24}
            style={{ border: '0.5px solid #CACFD8', borderRadius: '8px', padding: '6px 8px' }}
          >
            <Row gutter={[12, 0]} align={'middle'} wrap={false}>
              <Col>
                {['jpeg', 'png', 'jpg'].includes(attachment.extension) ? (
                  <Image
                    src={attachment.files?.ATTACHMENTS}
                    style={{ border: '0.5px solid #CACFD8', borderRadius: '4px' }}
                    height={32}
                    width={30}
                  />
                ) : (
                  <Button
                    type="link"
                    icon={getFileIcon(attachment.extension)}
                    download
                    title={attachment.fileName}
                    rel="noopener noreferrer"
                    href={attachment.files?.ATTACHMENTS}
                  />
                )}
              </Col>
              <Col flex={'auto'}>
                <Row>
                  <Col span={24}>
                    <Row align={'top'} wrap={false} gutter={8}>
                      <Col flex={'auto'}>
                        <Typography.Text ellipsis={{ tooltip: true }} className={'font-size-12 font-weight-500'}>
                          {attachment.fileName}
                        </Typography.Text>
                      </Col>
                      <Col>
                        <Popconfirm
                          title="Are you sure you want to delete this Attachment?"
                          onConfirm={() => onDelete(attachment?.id, attachment?.fileName)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            style={{ backgroundColor: '#EFF2F8' }}
                            type={'text'}
                            size={'small'}
                            icon={<DeleteIcon size={16} />}
                          />
                        </Popconfirm>
                      </Col>
                    </Row>
                  </Col>
                  <Col>
                    <Row align={'middle'} gutter={4}>
                      <Col>
                        <Typography.Text className={'font-size-12'}>
                          {formatFileSize(attachment.size || 0)}
                        </Typography.Text>
                      </Col>
                      <Col>
                        <Typography.Text ellipsis={{ tooltip: true }}>
                          <span className={styles.option_tag}>
                            {convertEnumsToReadableStrings(attachment.attachmentType)}
                          </span>
                        </Typography.Text>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        ))}
      </Row>
    </FMGModal>
  );
});
